const SAVE_KEY = 'diceSettlement_save';
const SAVE_VERSION = 1;

/**
 * Save game state to localStorage
 * @param {Object} gameState - Complete game state object
 * @returns {boolean} - Success status
 */
export function saveGame(gameState) {
  try {
    const saveData = {
      version: SAVE_VERSION,
      savedAt: new Date().toISOString(),
      state: gameState
    };

    const serialized = JSON.stringify(saveData);
    localStorage.setItem(SAVE_KEY, serialized);

    console.log('[SaveManager] Game saved successfully', {
      day: gameState.day,
      phase: gameState.phase
    });

    return true;
  } catch (error) {
    console.error('[SaveManager] Failed to save game:', error);

    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.error('[SaveManager] localStorage quota exceeded');
    }

    return false;
  }
}

/**
 * Load game state from localStorage
 * @returns {Object|null} - Game state or null if no valid save exists
 */
export function loadGame() {
  try {
    const serialized = localStorage.getItem(SAVE_KEY);

    if (!serialized) {
      return null;
    }

    const saveData = JSON.parse(serialized);

    // Version validation
    if (saveData.version !== SAVE_VERSION) {
      console.warn('[SaveManager] Save version mismatch. Attempting migration...');
      // Future: implement migration logic here
    }

    // Validate save data structure
    if (!saveData.state || typeof saveData.state !== 'object') {
      console.error('[SaveManager] Invalid save data structure');
      return null;
    }

    // Validate critical fields
    const state = saveData.state;
    if (typeof state.day !== 'number' ||
        !Array.isArray(state.workers) ||
        typeof state.phase !== 'string') {
      console.error('[SaveManager] Save data missing critical fields');
      return null;
    }

    console.log('[SaveManager] Game loaded successfully', {
      day: state.day,
      phase: state.phase,
      savedAt: saveData.savedAt
    });

    return {
      ...state,
      savedAt: saveData.savedAt
    };
  } catch (error) {
    console.error('[SaveManager] Failed to load game:', error);
    return null;
  }
}

/**
 * Check if a saved game exists
 * @returns {boolean}
 */
export function hasSavedGame() {
  try {
    const serialized = localStorage.getItem(SAVE_KEY);
    return serialized !== null;
  } catch (error) {
    console.error('[SaveManager] Failed to check for saved game:', error);
    return false;
  }
}

/**
 * Delete saved game
 * @returns {boolean} - Success status
 */
export function deleteSave() {
  try {
    localStorage.removeItem(SAVE_KEY);
    console.log('[SaveManager] Save deleted successfully');
    return true;
  } catch (error) {
    console.error('[SaveManager] Failed to delete save:', error);
    return false;
  }
}

/**
 * Get save metadata without loading full state
 * @returns {Object|null} - Save metadata or null
 */
export function getSaveMetadata() {
  try {
    const serialized = localStorage.getItem(SAVE_KEY);

    if (!serialized) {
      return null;
    }

    const saveData = JSON.parse(serialized);

    return {
      version: saveData.version,
      savedAt: saveData.savedAt,
      day: saveData.state?.day,
      phase: saveData.state?.phase
    };
  } catch (error) {
    console.error('[SaveManager] Failed to get save metadata:', error);
    return null;
  }
}
